import { memo } from 'react';

import { Link } from '../../../../../components/atoms/link/link';
import { ThresholdBarOverview } from '../../../../../components/molecules/threshold-bar-overview/threshold-bar-overview';
import { useIndicator } from '../../../context/indicators';
import { useAllRespondentTypes, useRenderContext, useSurveysByRespondentType } from '../../../context/render-context';
import { useReportContext } from '../../../renderer/report-context';
import { Answers } from '../../../survey-types';
import * as contentUtils from '../../../utils/content';
import * as createBlockUtils from '../../../utils/create-block';
import { ResolveFields } from '../../../utils/create-block';
import * as linkUtils from '../../../utils/links';
import { AnsweredStatus, useConditions } from '../../../utils/surveys/conditions';
import * as expressionUtils from '../../../utils/surveys/expressions';
import * as fieldUtils from '../../../utils/surveys/fields';
import { WithIndicator } from '../indicators/with-indicator';

type MeasuresProps = {
  readonly measures?: {
    expressionName?: string;
  }[];
  readonly allRespondents?: boolean;
  readonly displayOutcome?: boolean;
  readonly link?: string;
};

const RenderMeasureText = ({
  measureOutcomeText,
  displayOutcome,
  linkTo,
  measureName,
  conditionName,
}: {
  readonly measureName: string;
  readonly conditionName: string;
  readonly linkTo?: string;
  readonly measureOutcomeText?: string;
  readonly displayOutcome?: boolean;
}) => {
  const { navigateToPath } = useReportContext();

  const onClick = () => {
    if (linkTo) navigateToPath(linkTo);
  };
  return displayOutcome ? (
    <span className="px-1 border-typography">{measureOutcomeText && `${measureOutcomeText}`}</span>
  ) : (
    <Link asChild onClick={onClick}>
      <span className="px-1 lg:border-l-1 border-typography underline cursor-pointer">
        <strong>{measureName}</strong> <span className="text-nowrap">{conditionName}</span>
      </span>
    </Link>
  );
};

const useThresholdProps = (
  answers: Answers,
  expression: expressionUtils.QuestionExpression,
  displayOutcome: boolean
) => {
  const { answeredStatus } = useConditions(expression, answers);

  const value = expressionUtils.getMeasureValue(answers[expression.name]);

  const measureName = expression.measureName ?? expression.name;
  const conditionName = expression.conditionName ?? expression.name;

  const eValue = expression.getExpressionValue(value);
  const t = expressionUtils.getThresholdByScore(eValue, expression);
  const measureOutcomeText =
    displayOutcome && t.min?.text && answeredStatus === AnsweredStatus.COMPLETED ? t.min.text : undefined;

  return {
    answeredStatus,
    conditionName,
    measureName,
    measureOutcomeText,
    value,
  };
};

const MeasureForSurvey = memo(
  ({
    expression,
    respondentType,
    answers,
    showIndicators,
    displayOutcome,
    link,
  }: {
    readonly expression: expressionUtils.QuestionExpression;
    readonly respondentType: string;
    readonly answers: Answers;
    readonly showIndicators: boolean;
    readonly displayOutcome: boolean;
    readonly link: string;
  }) => {
    const indicator = useIndicator(respondentType);
    const { answeredStatus, conditionName, measureName, measureOutcomeText, value } = useThresholdProps(
      answers,
      expression,
      displayOutcome
    );

    return (
      <WithIndicator displayIndicator={showIndicators} respondentType={respondentType}>
        <ThresholdBarOverview
          answeredStatus={answeredStatus}
          appearance={showIndicators ? indicator?.indicatorAppearance : undefined}
          maxValue={expression.maxValue ?? 0}
          renderMeasureText={() => (
            <RenderMeasureText
              conditionName={conditionName}
              displayOutcome={displayOutcome}
              linkTo={link}
              measureName={measureName}
              measureOutcomeText={measureOutcomeText}
            />
          )}
          thresholds={(expression.thresholds || []).map((threshold) => ({
            label: threshold.text,
            value: Number.parseInt(threshold.value),
          }))}
          value={value}
        />
      </WithIndicator>
    );
  }
);

const Measure = memo(
  ({
    expressionName,
    allRespondents = false,
    link = '#',
    displayOutcome = false,
  }: {
    readonly expressionName: string;
    readonly allRespondents?: boolean;
    readonly displayOutcome?: boolean;
    readonly link?: string;
  }) => {
    const { allAnswers, selectedRespondentType } = useRenderContext();
    const allRespondentTypes = useAllRespondentTypes();
    const allSurveys = useSurveysByRespondentType();
    if (!selectedRespondentType) return <div>No respondentType selected</div>;

    const respondentTypes = allRespondents ? allRespondentTypes : [selectedRespondentType];

    return (
      <>
        {respondentTypes.map((respondentType) => {
          const survey = allSurveys[respondentType];
          const answers = allAnswers[respondentType];
          if (!survey) return <div key={respondentType}>No survey for {respondentType}</div>;
          if (!answers) return <div key={respondentType}>No answers for {respondentType}</div>;
          const expression = expressionUtils.getExpressionFromSurvey(survey, expressionName);
          if (!expression) return null;
          return (
            <MeasureForSurvey
              answers={answers}
              displayOutcome={displayOutcome}
              expression={expression}
              key={respondentType}
              link={link}
              respondentType={respondentType}
              showIndicators={allRespondents}
            />
          );
        })}
      </>
    );
  }
);

const Measures = ({ measures, ...rest }: MeasuresProps) => {
  const { survey } = useRenderContext();
  if (!survey) return <div>No survey</div>;
  if (!measures?.length) return <div>No measures added.</div>;

  return (
    <>
      {measures.map((measure, index) => {
        if (!measure.expressionName) return <div key={`${survey.id}-expression-${index}`}>No expression selected.</div>;
        const expression = expressionUtils.getExpressionFromSurvey(survey, measure.expressionName);

        if (!expression) return <div key={`${survey.id}-expression-${index}`}>No expression selected.</div>;

        return <Measure expressionName={measure.expressionName} key={`${measure.expressionName}-${index}`} {...rest} />;
      })}
    </>
  );
};

const resolveFields: ResolveFields<MeasuresProps> = ({ props: { survey, link } }, { fields, appState }) => {
  const allLinks = contentUtils.getAllAnchors(appState.data.content);
  return {
    ...fields,
    ...linkUtils.createLinkFields(allLinks, link === linkUtils.EXTERNAL_OPTION_VALUE),
    ...(survey && {
      measures: {
        type: 'array',
        label: 'Measures',
        arrayFields: {
          expressionName: fieldUtils.createExpressionField(survey, undefined),
        },
        getItemSummary: (item) => {
          const expression = expressionUtils.getExpressionFromSurvey(survey, item.expressionName);
          return expression?.measureName || item.expressionName || 'Select an expression';
        },
      },
    }),
    allRespondents: {
      type: 'radio',
      options: [
        { label: 'On', value: true },
        { label: 'Off', value: false },
      ],
    },
    displayOutcome: {
      type: 'radio',
      options: [
        { label: 'On', value: true },
        { label: 'Off', value: false },
      ],
    },
  };
};

const createMeasuresBlock = createBlockUtils.makeBlockFactory<MeasuresProps>(
  { withExampleAnswersJson: true },
  {
    label: 'Measures',
    render: Measures,
    resolveFields,
  }
);

export { createMeasuresBlock, Measures, type MeasuresProps, resolveFields };
